import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Pagination } from '@thinknimble/tn-models'
import { ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { ItemCollection, ListItemCard, ListItemRow } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { Spinner } from 'src/components/spinner'
import { Button } from 'src/components/button'
import { PaginationComponent } from 'src/components/pagination'
import { AddPropertyModal } from 'src/components/properties/add-property-modal'
import { ReportingStatusBadge } from 'src/components/properties/property-reporting-settings'
import { OrganizationStrip } from 'src/components/reservations/organization-strip'
import { organizationQueries } from 'src/services/user/queries'
import { propertyQueries } from 'src/services/properties/queries'
import { propertyApi } from 'src/services/properties/api'
import { useInfinitePagination } from 'src/hooks/use-infinite-pagination'
import { usePagination } from 'src/hooks/use-pagination'
import { useViewMode, type ViewMode } from 'src/hooks/use-view-mode'
import type { Property } from 'src/services/properties/models'
import type { OrganizationShape } from 'src/services/user/models'

const ORG_PAGE_SIZE = 6

function PropertyListItem({
  property,
  viewMode,
  onClick,
}: {
  property: Property
  viewMode: ViewMode
  onClick: () => void
}) {
  const { t } = useTranslation()
  const displayName = property.name || t('propertiesPage.unnamedProperty')
  const location = [property.city, property.country].filter(Boolean).join(', ')
  const address = property.formattedAddress || property.street || '-'
  const enabledLabel = (enabled?: boolean) =>
    enabled ? t('propertiesPage.enabled') : t('propertiesPage.disabled')

  // Whole item is the click target; keep keyboard access the old <button> gave.
  const interactiveProps = {
    onClick,
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    },
  }

  if (viewMode === 'list') {
    return (
      <ListItemRow {...interactiveProps} data-testid={`property-row-${property.id}`}>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-text-primary" title={displayName}>
            {displayName}
          </p>
          {location && (
            <p className="mt-0.5 truncate text-sm text-text-placeholder" title={location}>
              {location}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ReportingStatusBadge property={property} />
          <ChevronRightIcon
            className="h-5 w-5 flex-shrink-0 text-text-tertiary"
            aria-hidden="true"
          />
        </div>
      </ListItemRow>
    )
  }

  return (
    <ListItemCard {...interactiveProps} data-testid={`property-card-${property.id}`}>
      <div className="min-w-0">
        <h3 className="truncate text-lg font-bold text-text-primary" title={displayName}>
          {displayName}
        </h3>
        {location && (
          <p className="truncate text-sm text-text-placeholder" title={location}>
            {location}
          </p>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div className="col-span-2 min-w-0">
          <dt className="text-xs text-text-secondary">{t('propertiesPage.address')}</dt>
          <dd className="truncate font-medium text-text-primary" title={address}>
            {address}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('propertiesPage.tra')}</dt>
          <dd className="truncate font-medium text-text-primary">
            {enabledLabel(property.traEnabled)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('propertiesPage.sire')}</dt>
          <dd className="truncate font-medium text-text-primary">
            {enabledLabel(property.sireEnabled)}
          </dd>
        </div>
      </dl>

      <div className="mt-auto flex min-w-0 items-center justify-between gap-3 border-t border-border pt-3">
        <span className="shrink-0 text-sm text-text-secondary">
          {t('propertiesPage.reporting')}
        </span>
        <ReportingStatusBadge property={property} />
      </div>
    </ListItemCard>
  )
}

export function Properties() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [viewMode, setViewMode] = useViewMode('properties', 'list')

  const orgParam = searchParams.get('org')

  // ----- Organizations strip (infinite scroll + page-level pagination) -----
  const orgsInfinite = useInfinitePagination<OrganizationShape>({
    pageSize: ORG_PAGE_SIZE,
  })
  const { data: orgsResponse, isFetching: orgsFetching } = useQuery(
    organizationQueries.list({
      filters: { hasProperties: 'true' },
      pagination: orgsInfinite.pagination,
      paginationCallback: orgsInfinite.paginationCallback,
    }),
  )
  const { applyData: applyOrgsData, setIsFetching: setOrgsFetching } = orgsInfinite
  useEffect(() => {
    applyOrgsData(orgsResponse)
  }, [orgsResponse, applyOrgsData])
  useEffect(() => {
    setOrgsFetching(orgsFetching)
  }, [orgsFetching, setOrgsFetching])

  const organizations = orgsInfinite.items
  const orgsTotalCount = orgsResponse?.count ?? 0
  const orgsTotalPages = Math.max(
    1,
    orgsInfinite.pagination.calcTotalPages(orgsInfinite.pagination),
  )

  // Auto-pick first org when URL has none
  useEffect(() => {
    if (!orgParam && organizations.length > 0) {
      const next = new URLSearchParams(searchParams)
      next.set('org', organizations[0].id)
      setSearchParams(next, { replace: true })
    }
  }, [orgParam, organizations, searchParams, setSearchParams])

  // Property count per visible org
  const propertyCountQueries = useQueries({
    queries: organizations.map((org) => ({
      queryKey: ['properties', 'count', org.id],
      queryFn: async () => {
        const res = await propertyApi.list({
          filters: { organization: org.id },
          pagination: new Pagination({ size: 1 }),
        })
        return { orgId: org.id, count: res.count }
      },
      enabled: Boolean(org.id),
    })),
  })
  const propertyCountByOrg = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const q of propertyCountQueries) {
      if (q.data) counts[q.data.orgId] = q.data.count
    }
    return counts
  }, [propertyCountQueries])

  // Properties pagination
  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage } = usePagination()

  const {
    data: propertiesResponse,
    isLoading,
    isFetching,
  } = useQuery({
    ...propertyQueries.list({
      filters: orgParam ? { organization: orgParam } : undefined,
      pagination,
      paginationCallback,
    }),
    enabled: Boolean(orgParam),
  })

  const properties = propertiesResponse?.results ?? []
  const hasProperties = properties.length > 0

  const handleOrgSelect = (orgId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('org', orgId)
    setSearchParams(next)
  }

  const handleAddProperty = () => setIsModalOpen(true)

  const showOrgStrip = orgsTotalCount > 1
  const hasNoOrgs = !orgsFetching && organizations.length === 0 && orgsTotalCount === 0
  const showOrgPagination = orgsTotalPages > 1

  // Show spinner while orgs are loading and no org selected yet
  if (orgsFetching && !orgParam && organizations.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="md" />
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title={t('propertiesPage.title')}
        actions={
          <Button variant="primary" icon={<PlusIcon />} onClick={handleAddProperty}>
            {t('propertiesPage.addProperty')}
          </Button>
        }
      />
      <MainContent>
        <ContentPane>
          {hasNoOrgs ? (
            <div className="flex flex-col items-center justify-center py-16">
              <h2 className="text-xl font-semibold text-gray-700">
                {t('propertiesPage.noOrganizations')}
              </h2>
              <p className="mt-2 text-sm text-text-placeholder">
                {t('propertiesPage.createOrgHint')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {showOrgStrip ? (
                <section>
                  <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-placeholder">
                    {t('propertiesPage.organizations')}
                  </h2>
                  <OrganizationStrip
                    organizations={organizations}
                    propertyCountByOrg={propertyCountByOrg}
                    selectedOrgId={orgParam}
                    onSelect={handleOrgSelect}
                    isLoading={orgsFetching}
                    sentinelRef={orgsInfinite.sentinelRef}
                    isFetchingNext={orgsInfinite.isLoadingNextPage}
                    hasMore={orgsInfinite.pagination.hasNextPage}
                  />
                  {showOrgPagination ? (
                    <PaginationComponent
                      pagination={orgsInfinite.pagination}
                      isLoading={orgsFetching}
                      onNextPage={orgsInfinite.goToNextPage}
                      onPrevPage={orgsInfinite.goToPrevPage}
                      onGoToPage={orgsInfinite.goToPage}
                      showTotalCount={false}
                      className="mt-2"
                    />
                  ) : null}
                </section>
              ) : null}

              <section className="flex flex-col gap-4">
                <ListToolbar
                  count={propertiesResponse?.count}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
                {isLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Spinner size="md" />
                  </div>
                ) : hasProperties ? (
                  <>
                    <ItemCollection viewMode={viewMode}>
                      {properties.map((property) => (
                        <PropertyListItem
                          key={property.id}
                          property={property}
                          viewMode={viewMode}
                          onClick={() => navigate(`/properties/${property.id}`)}
                        />
                      ))}
                    </ItemCollection>
                    <PaginationComponent
                      pagination={pagination}
                      isLoading={isFetching}
                      onNextPage={goToNextPage}
                      onPrevPage={goToPrevPage}
                      onGoToPage={goToPage}
                      className="mt-4"
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16">
                    <h2 className="text-xl font-semibold text-gray-700">
                      {t('propertiesPage.noProperties')}
                    </h2>
                    <p className="mt-2 text-sm text-text-placeholder">
                      {t('propertiesPage.addFirstHint')}
                    </p>
                    <Button
                      variant="primary"
                      icon={<PlusIcon />}
                      onClick={handleAddProperty}
                      className="mt-4"
                    >
                      {t('propertiesPage.addProperty')}
                    </Button>
                  </div>
                )}
              </section>
            </div>
          )}
        </ContentPane>
      </MainContent>

      {orgParam && (
        <AddPropertyModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          ownerId={orgParam}
        />
      )}
    </>
  )
}
